/**
 * CareParamIcons — grafische Darstellung der Pflegeparameter (Vorlage:
 * "Kacheln und Icons CRM 2.0"-PDF). Zeigt Einschränkungen und Bedürfnisse
 * eines Kunden (bzw. Fähigkeiten einer BK) auf einen Blick als Icon-Zeile.
 *
 * Die Zuordnung erfolgt über die profile_options-IDs (stabil, sprachneutral):
 *   beweglichkeit  11 Rollator · 14 Rollstuhl · 15 Körperlift · 16 bettlägerig
 *   einschraenkungen 21 Demenz/Alzheimer
 *   geisteszustand 44/45 (leicht) dement
 *   sinneswahrnehmung 39 Hören · 40 Sehen · 41 Sprechen
 *   urinkontrolle 62/63, stuhlkontrolle 65/66 → Inkontinenz
 *   schlafverhalten 47/48 → nachts wach
 *   essen 52/53 → Hilfe beim Essen
 *   wohnsituation 70/71/73 → Einfamilienhaus · 72 → Mehrparteienhaus
 *   haustiere 86 → Haustiere
 *   raucher 111/112 bzw. bk_raucher 122/123 → (Nicht-)Raucher
 *   bk_fuehrerschein 124 → Führerschein
 *
 * Icons aus der PDF: Person(en), Rollstuhl, Demenz (Kopf+Puzzle), Haus,
 * Mehrparteienhaus, Körperlift, Haustiere, ♂/♀, Auto, (Nicht-)Raucher,
 * Familie. Fehlende Parameter im gleichen Strich-Stil ergänzt: Rollator,
 * Bett, Auge, Ohr, Sprechen, Tropfen (Inkontinenz), Mond (Nacht), Besteck.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getThemeColors } from '../../lib/theme';
import type { V1Profile } from '../../lib/v1';

export type CareIconKey =
  | 'person_1' | 'person_2' | 'familie' | 'familienleben'
  | 'rollator' | 'rollstuhl' | 'koerperlift' | 'bett'
  | 'demenz' | 'sehen' | 'hoeren' | 'sprechen'
  | 'inkontinenz' | 'nacht' | 'essen'
  | 'haus_efh' | 'haus_mfh' | 'haustiere'
  | 'raucher' | 'nichtraucher'
  | 'maennlich' | 'weiblich' | 'fuehrerschein';

// German fallback labels — kept for consumers outside the i18n rollout scope
// (e.g. matching/betreuung pages) that still read this map directly.
export const CARE_ICON_LABELS: Record<CareIconKey, string> = {
  person_1: '1-Personen-Haushalt',
  person_2: '2-Personen-Haushalt',
  familie: 'Familienanschluss',
  familienleben: 'Ausgeprägtes Familienleben',
  rollator: 'Rollator / Gehhilfe',
  rollstuhl: 'Rollstuhl',
  koerperlift: 'Körperlift',
  bett: 'Bettlägerig',
  demenz: 'Demenz',
  sehen: 'Sehen eingeschränkt',
  hoeren: 'Hören eingeschränkt',
  sprechen: 'Sprechen eingeschränkt',
  inkontinenz: 'Inkontinenz',
  nacht: 'Nachts wach',
  essen: 'Hilfe beim Essen',
  haus_efh: 'Einfamilienhaus',
  haus_mfh: 'Mehrparteienhaus',
  haustiere: 'Haustiere',
  raucher: 'Raucher',
  nichtraucher: 'Nichtraucher',
  maennlich: 'Mann',
  weiblich: 'Frau',
  fuehrerschein: 'Führerschein',
};

/** i18n key for each icon's label — used by the components below so the
 * icon row/legend render in the active UI language. */
export const CARE_ICON_LABEL_KEYS: Record<CareIconKey, string> = {
  person_1: 'care.icon_labels.person_1',
  person_2: 'care.icon_labels.person_2',
  familie: 'care.icon_labels.familie',
  familienleben: 'care.icon_labels.familienleben',
  rollator: 'care.icon_labels.rollator',
  rollstuhl: 'care.icon_labels.rollstuhl',
  koerperlift: 'care.icon_labels.koerperlift',
  bett: 'care.icon_labels.bett',
  demenz: 'care.icon_labels.demenz',
  sehen: 'care.icon_labels.sehen',
  hoeren: 'care.icon_labels.hoeren',
  sprechen: 'care.icon_labels.sprechen',
  inkontinenz: 'care.icon_labels.inkontinenz',
  nacht: 'care.icon_labels.nacht',
  essen: 'care.icon_labels.essen',
  haus_efh: 'care.icon_labels.haus_efh',
  haus_mfh: 'care.icon_labels.haus_mfh',
  haustiere: 'care.icon_labels.haustiere',
  raucher: 'care.icon_labels.raucher',
  nichtraucher: 'care.icon_labels.nichtraucher',
  maennlich: 'care.icon_labels.maennlich',
  weiblich: 'care.icon_labels.weiblich',
  fuehrerschein: 'care.icon_labels.fuehrerschein',
};

/** Translate an icon's label via the given `t` function (falls back to the German map). */
export function careIconLabel(k: CareIconKey, t: (key: string) => string): string {
  return t(CARE_ICON_LABEL_KEYS[k]) || CARE_ICON_LABELS[k];
}

/* ─── SVG-Icons (Strich-Stil wie components/icons, currentColor) ─────────── */

const S = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.7, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;

const ICONS: Record<CareIconKey, React.ReactNode> = {
  person_1: (<g><circle cx="12" cy="7.5" r="3.2"/><path d="M5.5 20c0-3.6 2.9-6 6.5-6s6.5 2.4 6.5 6"/></g>),
  person_2: (<g><circle cx="9" cy="8" r="2.8"/><path d="M3.5 19.5c0-3.1 2.5-5.2 5.5-5.2s5.5 2.1 5.5 5.2"/><circle cx="16.5" cy="8.5" r="2.3"/><path d="M16.7 14.6c2.4.4 4 2.3 4 4.9"/></g>),
  familie: (<g><circle cx="7" cy="7.5" r="2.4"/><path d="M3 19.5c0-2.8 1.8-4.6 4-4.6s4 1.8 4 4.6"/><circle cx="17" cy="7.5" r="2.4"/><path d="M13 19.5c0-2.8 1.8-4.6 4-4.6s4 1.8 4 4.6"/><circle cx="12" cy="12.5" r="1.8"/><path d="M9.6 19.5c0-1.9 1-3.3 2.4-3.3s2.4 1.4 2.4 3.3"/></g>),
  // Vater (links), zwei Kinder (Mitte), Mutter mit Kleid (rechts)
  familienleben: (<g><circle cx="3.7" cy="7.3" r="2.1"/><path d="M1.2 20v-6.2a2.5 2.5 0 0 1 5 0V20"/><circle cx="20.3" cy="7.3" r="2.1"/><path d="M17.8 20l1-6.6a1.5 1.5 0 0 1 3 0l1 6.6z"/><circle cx="9.3" cy="12.4" r="1.2"/><path d="M8.2 20v-3.4a1.1 1.1 0 0 1 2.2 0V20"/><circle cx="14.7" cy="12.4" r="1.2"/><path d="M13.6 20v-3.4a1.1 1.1 0 0 1 2.2 0V20"/></g>),
  rollator: (<g><path d="M7 5v9M15 5v9M7 8h8"/><circle cx="7" cy="17.5" r="1.8"/><circle cx="15" cy="17.5" r="1.8"/><path d="M7 5H5.5M15 5h-1.5"/></g>),
  rollstuhl: (<g><circle cx="10" cy="17" r="4.2"/><path d="M10 12.8V6.5m0 0h4.5l1.2 4h3.3M10 6.5a1.4 1.4 0 1 0 0-2.8 1.4 1.4 0 0 0 0 2.8zM16.5 14.5l1.8 4h2"/></g>),
  koerperlift: (<g><path d="M6 20V5h9"/><path d="M15 5v3"/><path d="M12.7 11.5h4.6l-.8 3.5h-3z"/><path d="M4.5 20h3"/></g>),
  bett: (<g><path d="M3 18V8m0 6h18v4m0-4v-2a3 3 0 0 0-3-3H10v5"/><circle cx="6.5" cy="10.8" r="1.6"/></g>),
  demenz: (<g><path d="M12.5 4a6.5 6.5 0 0 1 6.5 6.5c0 1.9-.8 3.5-2 4.7V20h-6a5 5 0 0 1-5-5v-1H4.5L6 10.6A6.5 6.5 0 0 1 12.5 4z"/><path d="M11.5 8.5h2v2h2v2h-2v2h-2v-2h-2v-2h2z"/></g>),
  sehen: (<g><path d="M3 12s3.2-5.5 9-5.5S21 12 21 12s-3.2 5.5-9 5.5S3 12 3 12z"/><circle cx="12" cy="12" r="2.4"/><path d="M5 19 19 5"/></g>),
  hoeren: (<g><path d="M8 9a4.5 4.5 0 0 1 9 0c0 3-2.5 3.6-2.5 6a3 3 0 0 1-3 3"/><path d="M6 14a7.5 7.5 0 0 1-.5-5"/><path d="M5 19 19 5"/></g>),
  sprechen: (<g><path d="M4 6.5h13v8H10l-3.5 3v-3H4z"/><path d="M5.5 18.5 18.5 5.5"/></g>),
  inkontinenz: (<g><path d="M12 4s5.5 6 5.5 10a5.5 5.5 0 0 1-11 0C6.5 10 12 4 12 4z"/></g>),
  nacht: (<g><path d="M19 14.5A7.5 7.5 0 0 1 9.5 5 7.5 7.5 0 1 0 19 14.5z"/></g>),
  essen: (<g><path d="M7 4v6a2 2 0 0 0 4 0V4M9 4v16"/><path d="M16 4c-1.5 1-2 3-2 5.5s.7 3 2 3.5V20"/></g>),
  haus_efh: (<g><path d="M4 11l8-7 8 7"/><path d="M6 9.5V20h12V9.5"/><path d="M10 20v-5h4v5"/></g>),
  haus_mfh: (<g><rect x="5" y="4" width="14" height="16"/><path d="M8 7.5h2m4 0h2M8 11.5h2m4 0h2M8 15.5h2m4 0h2"/><path d="M5 20h14"/></g>),
  haustiere: (<g><ellipse cx="12" cy="16" rx="4" ry="3"/><circle cx="7" cy="10.5" r="1.4"/><circle cx="10.3" cy="8" r="1.4"/><circle cx="13.7" cy="8" r="1.4"/><circle cx="17" cy="10.5" r="1.4"/></g>),
  raucher: (<g><rect x="3" y="14" width="14" height="3.4"/><path d="M14.5 14v3.4"/><path d="M17.5 10c1.5-1.5.3-2.6 1.2-4M20 11.5c1-1 .3-2 .9-3"/></g>),
  nichtraucher: (<g><rect x="4.5" y="13" width="12" height="3"/><path d="M13.8 13v3"/><circle cx="12" cy="14.5" r="8.2"/><path d="M6.2 8.7l11.6 11.6"/></g>),
  maennlich: (<g><circle cx="10" cy="14" r="4.8"/><path d="M13.5 10.5 19.5 4.5m0 0h-4.4m4.4 0v4.4"/></g>),
  weiblich: (<g><circle cx="12" cy="9" r="4.8"/><path d="M12 13.8V20m-2.8-2.8h5.6"/></g>),
  fuehrerschein: (<g><path d="M4 15l1.5-4.5A2 2 0 0 1 7.4 9h9.2a2 2 0 0 1 1.9 1.5L20 15v4h-2.2v-1.6H6.2V19H4z"/><circle cx="7.5" cy="15.7" r="0.9"/><circle cx="16.5" cy="15.7" r="0.9"/></g>),
};

/* ─── Ableitung der Icon-Keys aus einem Profil ───────────────────────────── */

function ids(v: unknown): number[] {
  if (v == null || v === '') return [];
  // BK-Beweglichkeit liegt als JSON-Array-String in einer String-Spalte.
  if (typeof v === 'string' && v.startsWith('[')) {
    try { v = JSON.parse(v); } catch { return []; }
  }
  if (Array.isArray(v)) return v.map(Number).filter(n => !isNaN(n));
  const n = Number(v);
  return isNaN(n) ? [] : [n];
}

/**
 * Leitet die aktiven Icons aus den Profilfeldern ab (PB wie BK).
 * Die Options-IDs entsprechen der profile_options-Tabelle (siehe Kopfkommentar).
 */
export function careIconKeysFromProfile(p: Partial<V1Profile> | null | undefined): CareIconKey[] {
  if (!p) return [];
  const keys: CareIconKey[] = [];
  const rec = p as Record<string, unknown>;

  // Geschlecht
  if (rec['anrede'] === 'Herr') keys.push('maennlich');
  else if (rec['anrede'] === 'Frau') keys.push('weiblich');

  // Haushalt (Personenzahl) — 0/leer = unbekannt
  // Eigenes Profilfeld, unabhängig von der Haushaltsgröße.
  if (rec['ausgepraegtes_familienleben']) keys.push('familienleben');

  const haushalt = Number(rec['haushalt'] ?? 0);
  if (haushalt >= 3) keys.push('familie');
  else if (haushalt === 2) keys.push('person_2');
  else if (haushalt === 1) keys.push('person_1');

  // Beweglichkeit
  const bew = ids(rec['beweglichkeit']);
  if (bew.includes(14)) keys.push('rollstuhl');
  if (bew.includes(15)) keys.push('koerperlift');
  if (bew.includes(16)) keys.push('bett');
  if (bew.some(v => v >= 11 && v <= 13)) keys.push('rollator');

  // Demenz — geisteszustand (leicht) dement ODER Einschränkung Demenz/Alzheimer
  const geist = ids(rec['geisteszustand']);
  if (geist.includes(44) || geist.includes(45) || ids(rec['einschraenkungen']).includes(21)) {
    keys.push('demenz');
  }

  // Sinneswahrnehmung
  const sinne = ids(rec['sinneswahrnehmung']);
  if (sinne.includes(39)) keys.push('hoeren');
  if (sinne.includes(40)) keys.push('sehen');
  if (sinne.includes(41)) keys.push('sprechen');

  // Inkontinenz (Urin oder Stuhl, ab "benötigt Einlage")
  const urin = ids(rec['urinkontrolle']);
  const stuhl = ids(rec['stuhlkontrolle']);
  if (urin.includes(62) || urin.includes(63) || stuhl.includes(65) || stuhl.includes(66)) {
    keys.push('inkontinenz');
  }

  // Nachts wach
  const schlaf = ids(rec['schlafverhalten']);
  if (schlaf.includes(47) || schlaf.includes(48)) keys.push('nacht');

  // Hilfe beim Essen
  const essen = ids(rec['essen']);
  if (essen.includes(52) || essen.includes(53)) keys.push('essen');

  // Wohnsituation
  const wohnen = ids(rec['wohnsituation']);
  if (wohnen.some(v => v === 70 || v === 71 || v === 73)) keys.push('haus_efh');
  if (wohnen.includes(72)) keys.push('haus_mfh');

  // Haustiere (86 = Ja)
  if (ids(rec['haustiere']).includes(86)) keys.push('haustiere');

  // Raucher — PB (raucher 112/111) oder BK (bk_raucher 122/123)
  const raucher = ids(rec['raucher']);
  const bkRaucher = ids(rec['bk_raucher']);
  if (raucher.includes(112) || bkRaucher.includes(122)) keys.push('raucher');
  else if (raucher.includes(111) || bkRaucher.includes(123)) keys.push('nichtraucher');

  // Führerschein (BK, 124 = Ja)
  if (ids(rec['bk_fuehrerschein']).includes(124)) keys.push('fuehrerschein');

  return keys;
}

/* ─── Darstellung ────────────────────────────────────────────────────────── */

export function CareParamIcon({ k, size = 18, c }: { k: CareIconKey; size?: number; c?: string }) {
  const { t } = useTranslation();
  const label = careIconLabel(k, t);
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24"
      role="img" aria-label={label}
      style={{ color: c ?? 'currentColor', display: 'block' }}
      {...S}
    >
      <title>{label}</title>
      {ICONS[k]}
    </svg>
  );
}

/**
 * Icon-Zeile für Kacheln/Drawer/Matching. Jedes Icon trägt sein deutsches
 * Label als Tooltip (title) und aria-label.
 *
 * `highlight`: Wenn gesetzt, werden Icons aus dieser Menge hervorgehoben
 * (Brand-Farbe + Chip) und die übrigen gedimmt — für das Matching-Board
 * (übereinstimmende Pflegeparameter zwischen PB und BK).
 */
export function CareParamIconRow({ keys, dark, size = 18, gap = 8, highlight }: {
  keys: CareIconKey[] | string[];
  dark: boolean;
  size?: number;
  gap?: number;
  highlight?: string[];
}) {
  const { t: tr } = useTranslation();
  const t = getThemeColors(dark);
  const valid = (keys as string[]).filter((k): k is CareIconKey => k in CARE_ICON_LABELS);
  if (valid.length === 0) return null;
  const hl = highlight ? new Set(highlight) : null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap, flexWrap: 'wrap' }}>
      {valid.map(k => {
        const matched = hl?.has(k) ?? false;
        const matchSuffix = hl ? ` — ${matched ? tr('care.matching.match') : tr('care.matching.no_match')}` : '';
        return (
          <span
            key={k}
            title={careIconLabel(k, tr) + matchSuffix}
            data-matched={hl ? String(matched) : undefined}
            style={{
              display: 'inline-flex',
              color: hl ? (matched ? '#b5297a' : t.muted) : t.heading,
              opacity: hl && !matched ? 0.45 : 1,
              background: matched ? (dark ? '#3a1a2d' : '#fce7f4') : 'transparent',
              borderRadius: 6,
              padding: hl ? 2 : 0,
            }}
          >
            <CareParamIcon k={k} size={size} />
          </span>
        );
      })}
    </div>
  );
}

/* ─── Legende (A12) ──────────────────────────────────────────────────────── */

/** Alle Icon-Keys in Anzeige-Reihenfolge (= Reihenfolge von CARE_ICON_LABELS). */
export const CARE_ICON_KEYS = Object.keys(CARE_ICON_LABELS) as CareIconKey[];

/**
 * Info-Icon neben der Spaltenüberschrift „Pflegeparameter" (/pflege).
 * Öffnet ein Popover mit allen Icons + Label aus CARE_ICON_LABELS.
 * Schließt per Klick außerhalb oder Escape.
 */
export function CareIconLegend({ dark, label }: { dark: boolean; label?: string }) {
  const { t: tr } = useTranslation();
  const resolvedLabel = label ?? tr('pflege.list.care_icons_legend');
  const t = getThemeColors(dark);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <span
      ref={wrapRef}
      style={{ position: 'relative', display: 'inline-flex', marginLeft: 6, verticalAlign: 'middle' }}
      // Klick darf nicht die Spalten-Sortierung des <th> auslösen.
      onClick={e => e.stopPropagation()}
    >
      <button
        type="button"
        aria-label={resolvedLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        style={{
          width: 18, height: 18, borderRadius: '50%', padding: 0,
          border: `1px solid ${t.border}`, background: t.inputBg, color: t.muted,
          fontSize: 11, fontWeight: 700, lineHeight: '16px', cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          textTransform: 'none', letterSpacing: 0,
        }}
      >i</button>
      {open && (
        <div
          role="dialog"
          aria-label={resolvedLabel}
          style={{
            position: 'absolute', top: 24, left: 0, zIndex: 20,
            background: t.card, border: `1px solid ${t.border}`, borderRadius: 10,
            boxShadow: dark ? '0 8px 24px rgba(0,0,0,0.45)' : '0 8px 24px -6px rgba(16,24,40,0.18)',
            padding: '10px 12px', width: 280, maxHeight: 360, overflowY: 'auto',
            textTransform: 'none', letterSpacing: 0, fontWeight: 400, cursor: 'default',
          }}
        >
          <div style={{ fontSize: 10.5, fontWeight: 700, color: t.muted, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
            {resolvedLabel}
          </div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {CARE_ICON_KEYS.map(k => (
              <li key={k} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '2px 0' }}>
                <span style={{ color: t.heading, display: 'inline-flex', flexShrink: 0 }}><CareParamIcon k={k} size={18} /></span>
                <span style={{ fontSize: 12.5, color: t.text }}>{careIconLabel(k, tr)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </span>
  );
}
